import React from 'react';
import { useTranslation } from 'react-i18next';
import StandardModal, { ModalSize } from '../Fundamentals/Modal/StandardModal';
import helpers from '../../Utils/helpers';

const TRACKING_PARAMS = [
    // HubSpot
    '_hsmi', '_hsenc', 'hsCtaTracking', 'hsCtaRedirect',
    // UTM (used by Google Analytics + many others)
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    // Other ad / email platforms (defensive)
    'fbclid', 'gclid', 'msclkid', 'mc_cid', 'mc_eid', 'mkt_tok'
];

function stripTracking(rawUrl) {
    try {
        const u = new URL(rawUrl);
        TRACKING_PARAMS.forEach((p) => u.searchParams.delete(p));
        return u.toString();
    } catch {
        return rawUrl;
    }
}

function isSafeHttpUrl(rawUrl) {
    try {
        const u = new URL(rawUrl);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
}

function CustomerEmailHistoryModal({ email, onClose }) {
    const { t } = useTranslation();

    const timeline = email?.timeline || [];

    const eventLabel = (type) => {
        if (!type) return '';
        const key = `customeremails.history.${type.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? type : translated;
    };

    const eventBadgeClass = (type) => {
        switch ((type || '').toUpperCase()) {
            case 'SENT': return 'label label-success';
            case 'DELIVERED': return 'label label-success';
            case 'PROCESSED': return 'label label-info';
            case 'OPEN': return 'label label-info';
            case 'CLICK': return 'label label-info';
            case 'DEFERRED': return 'label label-warning';
            case 'BOUNCE':
            case 'DROPPED': return 'label label-danger';
            default: return 'label label-default';
        }
    };

    const deviceLabel = (deviceType) => {
        if (!deviceType) return '';
        const key = `customeremails.history.device.${deviceType.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? deviceType : translated;
    };

    const deviceIconClass = (deviceType) => {
        switch ((deviceType || '').toUpperCase()) {
            case 'COMPUTER': return 'vismaicon vismaicon-sm vismaicon-laptop';
            case 'MOBILE':   return 'vismaicon vismaicon-sm vismaicon-platform-mobile';
            // No dedicated tablet icon in VUD — fall back to the mobile-device one
            case 'TABLET':   return 'vismaicon vismaicon-sm vismaicon-platform-mobile';
            default: return null;
        }
    };

    const renderDetail = (entry) => {
        const upperType = (entry.type || '').toUpperCase();
        if (!entry.detail) return null;

        if (upperType === 'CLICK') {
            // Only render as a link if the URL uses http(s); anything else (javascript:, data:, ...)
            // gets rendered as plain text so a malicious email payload can't produce a clickable
            // dangerous link in the admin UI.
            if (!isSafeHttpUrl(entry.detail)) {
                return (
                    <span style={{ wordBreak: 'break-all', display: 'inline-block', maxWidth: '100%' }}>
                        {entry.detail}
                    </span>
                );
            }
            // Display the original URL (what the customer actually clicked) but link to a cleaned
            // version with utm_*, _hsmi, and other tracking params stripped — so opening the link
            // from Skynet doesn't pollute analytics or re-fire HubSpot attribution.
            const cleaned = stripTracking(entry.detail);
            const stripped = cleaned !== entry.detail;
            return (
                <a
                    href={cleaned}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    title={stripped ? t('customeremails.history.linkOpenNoTracking') : entry.detail}
                    style={{ wordBreak: 'break-all', display: 'inline-block', maxWidth: '100%' }}
                >
                    {entry.detail}
                </a>
            );
        }
        if (upperType === 'OPEN') {
            const iconClass = deviceIconClass(entry.detail);
            const label = deviceLabel(entry.detail);
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    {iconClass && <span className={iconClass} aria-hidden="true" style={{ marginRight: 6 }} />}
                    {label}
                </span>
            );
        }
        return <span style={{ wordBreak: 'break-word' }}>{entry.detail}</span>;
    };

    const title = `${t('customeremails.history.title')} — ${email?.subject || ''}`;

    return (
        <StandardModal title={title} size={ModalSize.LARGE} onClose={onClose}>
            {timeline.length === 0 ? (
                <p>{t('customeremails.history.empty')}</p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-stripe">
                        <thead>
                            <tr>
                                <th className="text-left">{t('customeremails.history.event')}</th>
                                <th className="text-left">{t('customeremails.history.detail')}</th>
                                <th className="text-left">{t('customeremails.history.date')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {timeline.map((entry, index) => {
                                const isOpen = (entry.type || '').toUpperCase() === 'OPEN';
                                const badgeTitle = isOpen && entry.detail ? deviceLabel(entry.detail) : '';
                                return (
                                    <tr key={index}>
                                        <td className="text-left" style={{ width: '160px' }}>
                                            <span className={eventBadgeClass(entry.type)} style={{ whiteSpace: 'nowrap' }} title={badgeTitle}>
                                                {eventLabel(entry.type)}
                                            </span>
                                        </td>
                                        <td className="text-left" style={{ maxWidth: '400px' }}>
                                            {renderDetail(entry)}
                                        </td>
                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                            {helpers.formatDateAndTime(entry.occurredAt)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </StandardModal>
    );
}

export default CustomerEmailHistoryModal;
